import { apiFormaleta, type DestinoFoto, type FotoResultado } from '@/api/formaleta';
import { comprimirFoto } from './comprimirFoto';

/**
 * Sube la foto de una pieza o un renglón por el mejor camino disponible.
 *
 * Son dos mejoras de F-3 y conviene no confundirlas, porque una depende de
 * configuración de Azure y la otra no:
 *
 *  1. **Comprimir** (siempre activo, cero infraestructura). 3–6 MB de la cámara
 *     pasan a ~250 KB. Es de lejos la mejora más grande para una obra con una
 *     barra de señal, y pasa por cualquiera de los dos caminos.
 *  2. **Subir directo al blob** (se enciende con una regla CORS en la cuenta de
 *     Storage). Saca los bytes del Function App, que en Consumo cobra por
 *     GB-s mientras hace de tubo.
 *
 * El orden importa: se INTENTA el carril directo y ante cualquier falla se cae
 * al endpoint de siempre. Por eso esto se puede desplegar hoy, con el CORS
 * todavía vacío: el PUT directo va a fallar en el navegador, el fallback va a
 * subir la foto igual (ya comprimida), y el día que se agregue la regla CORS el
 * carril rápido se enciende solo, sin tocar código.
 *
 * Nunca lanza por un fallo del carril directo: una foto que no subió no puede
 * convertir en error una pieza que YA quedó inventariada.
 */
export interface ResultadoSubida extends FotoResultado {
  /** Por dónde entró al final. Sirve para saber si el CORS ya está surtiendo efecto. */
  via: 'directo' | 'backend';
  bytesOriginal: number;
  bytesSubidos: number;
}

export async function subirFotoFormaleta(
  tipo: DestinoFoto,
  id: number,
  original: Blob,
): Promise<ResultadoSubida> {
  const { archivo, bytesOriginal, bytesFinal } = await comprimirFoto(original);

  const directo = await intentarDirecto(tipo, id, archivo);
  if (directo) return { ...directo, via: 'directo', bytesOriginal, bytesSubidos: bytesFinal };

  const res = tipo === 'pieza'
    ? await apiFormaleta.fotoPieza(id, archivo)
    : await apiFormaleta.fotoRenglon(id, archivo);
  return { ...res, via: 'backend', bytesOriginal, bytesSubidos: bytesFinal };
}

/**
 * Devuelve null —sin ruido— en cuanto algo no da: sin firma, PUT rechazado por
 * CORS, red cortada. El `null` significa exactamente «probá el otro camino».
 */
async function intentarDirecto(
  tipo: DestinoFoto,
  id: number,
  archivo: Blob,
): Promise<FotoResultado | null> {
  try {
    const firma = await apiFormaleta.fotoFirma(tipo, id, archivo.type || 'image/jpeg');
    if (!firma.nombre || !firma.url) return null;

    const put = await fetch(firma.url, {
      method: 'PUT',
      // Sin este encabezado Azure rechaza el PUT a un blob de bloques.
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': firma.contentType || archivo.type || 'image/jpeg',
      },
      body: archivo,
    });
    if (!put.ok) return null;

    // Recién con el blob arriba se anota en la fila: al revés, una fila podría
    // quedar apuntando a un blob que nunca llegó.
    return await apiFormaleta.fotoAsignar(tipo, id, firma.nombre);
  } catch {
    return null;
  }
}
